import axios from "axios";
import type { InternalAxiosRequestConfig } from "axios";
import { getDict, getLang } from "../i18n";
import { clearSession, getSession, updateSession } from "./auth";

/** Backend base URL, e.g. http://localhost:5000/api — set VITE_APIBASE_URL in .env. */
const BASE_URL = import.meta.env.VITE_APIBASE_URL as string | undefined;

export const api = axios.create({
  baseURL: BASE_URL,
  // Free ngrok tunnels answer browsers with a warning page (no CORS headers)
  // unless this header is sent; only added when the API is behind ngrok.
  headers: BASE_URL?.includes("ngrok") ? { "ngrok-skip-browser-warning": "true" } : undefined,
});

const SERVER_URL = (import.meta.env.VITE_SERVER_BASE_URL as string | undefined) ?? "";

/** Full URL of a file the backend serves, e.g. "/uploads/listings/a.jpg". */
export const assetUrl = (path: string) => (/^https?:/.test(path) ? path : `${SERVER_URL}${path}`);

/** What every failed API call rejects with: the backend's message + HTTP status. */
export class ApiError extends Error {
  readonly status?: number;
  /** Per-field messages from a 400 validation error, e.g. { brand: "Required" }. */
  readonly errors?: Record<string, string>;
  /** Machine-readable reason, e.g. "EMAIL_NOT_VERIFIED". */
  readonly code?: string;

  constructor(message: string, status?: number, errors?: Record<string, string>, code?: string) {
    super(message);
    this.status = status;
    this.errors = errors;
    this.code = code;
  }
}

/**
 * What to tell the user about a failed call: the per-field messages when the
 * backend sent some ("SIRET must be 14 digits"), not just "Validation failed".
 */
export function errorText(err: unknown): string {
  if (err instanceof ApiError && err.errors && Object.keys(err.errors).length) {
    return Object.values(err.errors).join(" · ");
  }
  return err instanceof Error ? err.message : getDict().common.somethingWrong;
}

/** Moves the cursor to the first field marked invalid, once it has re-rendered. */
export function focusFirstError() {
  requestAnimationFrame(() =>
    document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
  );
}

// The backend translates its messages from X-Language; protected routes need the token.
api.interceptors.request.use((config) => {
  config.headers.set("X-Language", getLang());
  const token = getSession()?.token;
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

let refreshing: Promise<string> | null = null;

/** Swaps the refresh token for a new access token; parallel 401s share one call. */
function refreshAccessToken(): Promise<string> {
  refreshing ??= (async () => {
    const refreshToken = getSession()?.refreshToken;
    if (!refreshToken) throw new Error("No refresh token");
    const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
      `${BASE_URL}/auth/refresh`,
      { refreshToken },
    );
    updateSession({ token: data.accessToken, refreshToken: data.refreshToken });
    return data.accessToken;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/**
 * On an expired access token, refreshes it once and replays the request.
 * Other errors are rethrown with the backend's `message`, so forms can show it as is.
 */
api.interceptors.response.use(undefined, async (err) => {
  if (axios.isAxiosError(err)) {
    const config = err.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const code = (err.response?.data as { code?: string } | undefined)?.code;

    if (err.response?.status === 401 && code === "TOKEN_EXPIRED" && config && !config._retried) {
      config._retried = true;
      try {
        const token = await refreshAccessToken();
        config.headers.set("Authorization", `Bearer ${token}`);
        return api(config);
      } catch {
        // Refresh token expired or revoked: the user has to log in again.
        clearSession();
      }
    }
  }

  const response = axios.isAxiosError(err) ? err.response : undefined;
  const data = response?.data as
    | { message?: string; errors?: Record<string, string>; code?: string; field?: string }
    | undefined;
  const message = data?.message ?? getDict().common.somethingWrong;
  // 409 duplicates name one field ({ message, field: "email" }): treat it like a field error
  const errors = data?.errors ?? (data?.field ? { [data.field]: message } : undefined);
  return Promise.reject(new ApiError(message, response?.status, errors, data?.code));
});
